/**
 * TrackerV2 核心类
 * 职责：统一的埋点入口，集成数据增强、校验、上报流程
 */

import { exposeBuffer } from "../buffer/ExposeBuffer";
import { NavigationContext } from "../context/NavigationContext";
import { enricherManager } from "../enricher/EnricherManager";
import type { EnrichOptions } from "../enricher/types";
import { eventBus, TrackEvent } from "../event/EventBus";
import { reporter } from "../reporter/Reporter";
import { TrackerLogger } from "../utils/logger";
import { validator } from "../validator/Validator";

export interface TrackOptions {
  targetNode?: Element | null;
  enrich?: boolean; // 是否启用数据增强
  validate?: boolean; // 是否启用数据校验
  enableLegacy?: boolean; // 是否同时上报到旧架构（双写）
  enrichOptions?: EnrichOptions; // 数据增强选项
}

// 事件ID映射
const EVENT_ID_MAP: Record<string, number> = {
  button_click: 39008,
  material_click: 33002,
  board_click: 33008,
  topic_click: 35004,
  people_click: 36002,
  content_expose: 33009,
  board_expose: 33007,
  topic_expose: 35003,
  people_expose: 36001,
  resource_loc_click: 35002,
  resource_loc_expose: 35001,
  dialog_action: 39013,
  dialog_expose: 39012,
  questionnaire_click: 39028,
  questionnaire_expose: 39029,
  button_expose: 39024,
  search: 39005,
  trackPageView: 39003,
  login: 39001,
  register: 39004,
  upload: 39043,
  pay_expose: 32001,
  switch_version: 39008,
};

export class TrackerV2 {
  private sessionId: string | null = null;

  /**
   * 上报埋点事件
   */
  track(
    eventName: string,
    data: Record<string, any> = {},
    options: TrackOptions = {}
  ): void {
    const {
      targetNode,
      enrich = true,
      validate = true,
      enableLegacy = false,
      enrichOptions,
    } = options;

    try {
      // 1. 获取基础数据
      let trackData: Record<string, any> = {
        ...data,
        event_name: eventName, // 添加 event_name，供 Enricher 使用
        event: eventName, // 兼容旧版字段名
      };

      // 2. 获取页面信息
      const page = this.getPage();
      trackData = {
        ...trackData,
        ...page,
      };

      // 3. 数据增强
      if (enrich) {
        trackData = enricherManager.enrich(
          trackData,
          targetNode,
          page,
          enrichOptions
        );
      }

      // 4. 构建事件对象
      // ⚠️ 重要：从 trackData 中移除 event_name 和 event，避免在 detail 中重复
      // 这些字段已经在事件对象的顶层（event.event_id 和 event.event_name）
      // 它们只是临时添加到 trackData 中供 Enricher 使用
      const { event_name: _, event: __, ...cleanTrackData } = trackData;

      const event: TrackEvent = {
        event_id: this.getEventId(eventName),
        event_name: eventName,
        data: cleanTrackData,
        timestamp: Date.now(),
        metadata: {
          source: "v2",
          sessionId: this.getSessionId(),
          traceId: this.generateTraceId(),
        },
      };

      // 5. 数据校验
      if (validate) {
        const validation = validator.validate(eventName, trackData);
        TrackerLogger.validate(
          eventName,
          validation.valid,
          validation.errors,
          validation.warnings
        );
      }

      // 6. 特殊处理：content_expose 事件需要聚合上报
      if (eventName === "content_expose") {
        // 添加到曝光缓冲区，由缓冲区负责聚合和上报
        exposeBuffer.add(trackData);
        return;
      }

      // 7. 发送到事件总线
      eventBus.emit(event);

      // 8. 上报
      reporter.report(event);

      // 9. 打印完整事件日志（便于验证）
      TrackerLogger.event(eventName, trackData, "hb_web_test");
    } catch (error) {
      TrackerLogger.error("TrackerV2", `上报失败: ${eventName}`, error);

      // 生产环境不应该因为埋点错误而影响业务
      if (process.env.NODE_ENV === "production") {
        // 可以上报到监控系统
        this.reportError(error, eventName, data);
      }
    }
  }

  /**
   * 获取事件 ID
   */
  private getEventId(eventName: string): number {
    return EVENT_ID_MAP[eventName] || 0;
  }

  /**
   * 获取页面信息
   * 策略：
   * 1. 优先从 NavigationContext 获取（用于获取 referrer、source_page 等导航上下文）
   * 2. 验证 NavigationContext 的页面信息是否与当前 URL 匹配
   * 3. 如果不匹配，从 URL 直接解析（确保获取当前页面的准确信息）
   *
   * 这样既保持了 NavigationContext 的设计初衷（管理导航上下文），
   * 又确保了页面信息的准确性（即使 NavigationContext 没有及时更新）
   */
  private getPage(): { page_nm: string; sub_page_nm?: string } {
    // 先从 URL 解析当前页面信息（确保准确性）
    let currentPageInfo: { page_nm: string; sub_page_nm?: string } | null =
      null;
    try {
      const { getPageInfo } = require("../utils/getPageInfo");
      currentPageInfo = getPageInfo();
    } catch (e) {
      TrackerLogger.error("TrackerV2", "从 URL 解析页面信息失败", e);
    }

    // 从 NavigationContext 获取（用于导航上下文）
    try {
      const navContext = NavigationContext.get();
      if (navContext && navContext.current_page_nm) {
        // 验证 NavigationContext 的页面信息是否与当前 URL 匹配
        if (
          currentPageInfo &&
          currentPageInfo.page_nm &&
          navContext.current_page_nm !== currentPageInfo.page_nm
        ) {
          // 不匹配，立即更新 NavigationContext，然后使用 URL 解析的结果
          // 立即更新 NavigationContext，确保后续调用能获取到正确的值
          NavigationContext.onPageChange(
            window.location.href,
            currentPageInfo.page_nm,
            currentPageInfo.sub_page_nm
          );

          TrackerLogger.warn(
            "TrackerV2",
            `NavigationContext 页面信息不匹配: ${navContext.current_page_nm} vs ${currentPageInfo.page_nm}，已更新 NavigationContext 并使用 URL 解析结果`
          );
          return {
            page_nm: currentPageInfo.page_nm,
            sub_page_nm: currentPageInfo.sub_page_nm,
          };
        }

        // 匹配，使用 NavigationContext 的结果
        return {
          page_nm: navContext.current_page_nm,
          sub_page_nm: navContext.current_sub_page_nm,
        };
      }
    } catch (e) {
      TrackerLogger.error(
        "TrackerV2",
        "从 NavigationContext 获取页面信息失败",
        e
      );
    }

    // NavigationContext 未初始化时，使用 URL 解析的结果
    if (currentPageInfo && currentPageInfo.page_nm) {
      TrackerLogger.debug(
        "TrackerV2",
        "NavigationContext 未初始化，使用 URL 解析结果",
        currentPageInfo
      );
      return {
        page_nm: currentPageInfo.page_nm,
        sub_page_nm: currentPageInfo.sub_page_nm,
      };
    }

    // 最终兜底：返回空值
    TrackerLogger.warn("TrackerV2", "无法获取页面信息，返回空值");
    return { page_nm: "", sub_page_nm: "" };
  }

  /**
   * 获取会话 ID
   */
  private getSessionId(): string {
    if (!this.sessionId) {
      this.sessionId = `tracker_v2_${Date.now()}_${Math.random()
        .toString(36)
        .slice(2)}`;
    }
    return this.sessionId;
  }

  /**
   * 生成追踪 ID
   */
  private generateTraceId(): string {
    return `trace_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }

  /**
   * 上报错误到监控系统
   */
  private reportError(
    error: any,
    eventName: string,
    data: Record<string, any>
  ): void {
    try {
      if (typeof window !== "undefined" && (window as any).Sentry) {
        (window as any).Sentry.captureException(error, {
          tags: {
            eventName,
            module: "tracker-v2",
          },
          extra: {
            data,
          },
        });
      }
    } catch (e) {
      // 降级处理
    }
  }

  /**
   * 立即上报缓冲区中的所有事件
   */
  flush(): void {
    reporter.flush();
  }
}

// 导出单例
export const trackerV2 = new TrackerV2();

// 挂载到window对象（用于调试和旧代码兼容）
if (typeof window !== "undefined") {
  (window as any).trackerV2 = trackerV2;
}
